import type { FormConfig } from '@axo/product/@types/form.config.types';
import { create } from 'zustand';
import {
  createJSONStorage,
  devtools,
  persist,
  subscribeWithSelector,
} from 'zustand/middleware';
import type {
  ApplicationStateStore,
  ApplicationStateStoreState,
  Step,
} from './applicationStateStore.types';

const initialState: ApplicationStateStoreState = {
  i18n: {
    namespace: 'common',
  },
  hasBeenRedirected: false,
  isLoading: false,
  isError: false,
  isSuccess: false,
  isComplete: false,
  currentStep: 0,
  isPreForm: false,
  isCoApplicant: undefined,
  steps: [],
  coAppSteps: undefined,
  fieldsConfig: {},
};

const storeName = 'axo/application-state';

/**
 * @description
 * Store used to manage the state of the loan application.
 */
export const useApplicationStateStore = create<ApplicationStateStore>()(
  subscribeWithSelector(
    devtools(
      persist(
        (set) => ({
          ...initialState,
          setIsLoading: (isLoading) =>
            set({ isLoading }, undefined, 'setIsLoading'),
          setError: (isError: boolean) =>
            set({ isError }, undefined, 'setError'),
          setSuccess: (isSuccess: boolean) =>
            set({ isSuccess }, undefined, 'setSuccess'),
          setComplete: (isComplete: boolean) =>
            set({ isComplete }, undefined, 'setComplete'),

          setHasBeenRedirected: (hasBeenRedirected: boolean) =>
            set({ hasBeenRedirected }, undefined, 'setHasBeenRedirected'),

          setI18n: (i18n) =>
            set(
              () => ({
                i18n,
              }),
              undefined,
              'setI18n'
            ),

          setSteps: (steps: Step[]) =>
            set(() => ({ steps }), undefined, 'setSteps'),
          setCoAppSteps: (coAppSteps: Step[]) =>
            set(() => ({ coAppSteps }), undefined, 'setCoAppSteps'),

          setFieldsConfig: (fieldsConfig: FormConfig['fields']) =>
            set({ fieldsConfig }, undefined, 'setFieldsConfig'),

          goToNextStep: () =>
            set(
              ({ steps, currentStep, isPreForm }) => {
                const isLastStep =
                  isPreForm || currentStep === steps?.length - 1;

                return {
                  currentStep: isLastStep
                    ? currentStep // Stay on the last step
                    : currentStep + 1,
                  isComplete: isLastStep,
                };
              },
              undefined,
              'goToNextStep'
            ),
          goToPreviousStep: () =>
            set(
              ({ currentStep }) => ({
                currentStep: Math.max(currentStep - 1, 0),
              }),
              undefined,
              'goToPreviousStep'
            ),
          goToStep: (step: number) =>
            set(
              ({ steps }) => ({
                currentStep: Math.max(0, Math.min(step, steps.length || 1)),
              }),
              undefined,
              'goToStep'
            ),

          setIsPreForm: (isPreForm: boolean) => {
            set(
              {
                isPreForm,
                isComplete: false,
                // FIXED conflicts with direct navigation to specific step
                // currentStep: 0,
              },
              undefined,
              'setIsPreForm'
            );
          },
          setIsCoApplicant: (isCoApplicant?: boolean) =>
            set({ isCoApplicant }, undefined, 'setIsCoApplicant'),

          setIsComplete: (isComplete: boolean) =>
            set({ isComplete }, undefined, 'setIsComplete'),

          reset: () => set({ ...initialState }, undefined, 'reset'),
        }),
        {
          name: storeName,
          storage: createJSONStorage(() => sessionStorage),
        }
      ),
      {
        name: storeName,
      }
    )
  )
);
